import { fs } from '@/theme/tokens';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Box, Typography, Button, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { APP_BASE } from '@/lib/api-base';
import { isChunkLoadError, reloadForNewVersion } from '@/lib/chunk-reload';
import { StaleVersionCard } from './StaleVersionCard';

type Props = { children: ReactNode };
type State = { error: Error | null; info: ErrorInfo | null; showDetails: boolean; copied: boolean };

/**
 * Page-scoped error boundary.
 *
 * Wraps each route element in routes.tsx. When a render or lifecycle method
 * throws, this catches it and shows a recovery card instead of leaving the
 * shell blank. The user can retry the same route, go home, or expand to see
 * the stack trace (useful for bug reports).
 *
 * The error is also logged to console with a `[ErrorBoundary]` tag so it
 * shows up in browser-side observability tools that filter on prefix.
 *
 * Why a class component: React's error-boundary contract still requires
 * `componentDidCatch` + `getDerivedStateFromError`. There is no hook
 * equivalent yet (as of React 19).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, showDetails: false, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ info });
    // A page chunk that a deploy removed. lazyRoute normally reloads before
    // this point; this catches any import that did not go through it.
    if (isChunkLoadError(error)) reloadForNewVersion();
  }

  reset = () => {
    this.setState({ error: null, info: null, showDetails: false, copied: false });
  };

  copyDetails = () => {
    const err = this.state.error;
    const text = [
      `${err?.name || 'Error'}: ${err?.message || 'Unknown error'}`,
      err?.stack || '',
      this.state.info?.componentStack ? `\nComponent stack:${this.state.info.componentStack}` : '',
    ].filter(Boolean).join('\n');
    const done = () => { this.setState({ copied: true }); window.setTimeout(() => this.setState({ copied: false }), 2000); };
    try {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } catch { done(); }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    if (isChunkLoadError(err)) return <StaleVersionCard />;
    const { showDetails, copied } = this.state;
    return (
      <Box sx={{
        minHeight: '58vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        px: 2, py: 6,
        animation: 'uknErrIn 320ms cubic-bezier(0.16,1,0.3,1) both',
        '@keyframes uknErrIn': {
          from: { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
          to: { opacity: 1, transform: 'none' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}>
        <Box sx={{
          width: 76, height: 76, borderRadius: '50%',
          display: 'grid', placeItems: 'center', mb: 2.5,
          bgcolor: (t) => `${t.palette.error.main}14`,
          color: 'error.main',
          boxShadow: (t) => `0 0 0 10px ${t.palette.error.main}0d`,
        }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Something went wrong
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 460, mb: 3 }}>
          This page hit an unexpected error. Your data is safe. Only the view
          failed to render. Try again, and if it keeps happening, copy the
          details below and share them with support.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}
          sx={{ mb: 2.5, width: { xs: '100%', sm: 'auto' }, maxWidth: 420 }}>
          <Button variant="contained" size="large" startIcon={<RefreshIcon />}
            onClick={this.reset} fullWidth>
            Try again
          </Button>
          <Button variant="outlined" size="large" startIcon={<HomeIcon />}
            onClick={() => { this.reset(); window.location.href = `${APP_BASE}/`; }}
            fullWidth>
            Go to dashboard
          </Button>
        </Stack>

        <Box sx={{
          width: '100%', maxWidth: 560, mx: 'auto', textAlign: 'left',
          border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden',
          bgcolor: 'background.paper',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25,
            bgcolor: 'action.hover' }}>
            <Box sx={{ flex: 1, minWidth: 0, fontSize: fs.small, fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {err.name || 'Error'}: {err.message || 'Unknown error'}
            </Box>
            <Button size="small" color="inherit"
              startIcon={copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
              onClick={this.copyDetails} sx={{ flexShrink: 0 }}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="small" color="inherit" onClick={() => this.setState({ showDetails: !showDetails })}
              sx={{ flexShrink: 0 }}>
              {showDetails ? 'Hide details' : 'Details'}
            </Button>
          </Box>
          {showDetails && (
            <Box sx={{
              p: 1.75, fontSize: fs.tiny, lineHeight: 1.6,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 320, overflow: 'auto', fontFamily: 'ui-monospace, monospace',
              color: 'text.secondary' }}>
              {err.stack ?? '(no stack trace)'}
              {this.state.info?.componentStack && (
                <>
                  {'\n\nComponent stack:'}
                  {this.state.info.componentStack}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
}
